import type {
    ApiSection,
    BuyerBriefOverviewResponse,
    GeneratedContentItem,
    IdealCandidateData,
    SeededSection,
} from '../types';
import { mapSectionType } from './mapSectionType';

interface MapperParams {
    section: ApiSection;
    brief: BuyerBriefOverviewResponse;
    generatedContent: GeneratedContentItem[];
}

export const mapApiSectionToSeeded = ({ section, brief, generatedContent }: MapperParams): SeededSection => {
    if (section.id === 'ideal_candidate') {
        return mapIdealCandidateSection(section, brief);
    }

    return mapGeneratedSection(section, generatedContent);
};

const mapIdealCandidateSection = (section: ApiSection, brief: BuyerBriefOverviewResponse): SeededSection => {
    const prefs = brief.metadataAnswers?.freelancerPreferences || {};
    const topics = brief.metadataAnswers?.serviceOptions?.flatMap((opt) => opt.values) || [];

    const idealCandidateObject: IdealCandidateData = {
        skillsAndExperience: topics,
        languages: prefs.languages || [],
        countriesAndRegions: prefs.countries || prefs.regions || [],
        timezone: prefs.timezone || null,
        freelancerType: prefs.freelancerType || {},
    };

    return {
        id: section.id,
        title: section.name ?? section.id,
        included: section.included ?? true,
        mandatory: section.mandatory ?? false,
        readonly: section.readonly ?? false,
        editing_title: section.editing_title ?? null,
        placeholder: section.placeholder ?? undefined,
        type: mapSectionType(section.type),
        content: topics.join(', ') || '',
        idealCandidateData: idealCandidateObject,
    };
};

const mapGeneratedSection = (section: ApiSection, generatedContent: GeneratedContentItem[]): SeededSection => {
    const generatedMatch = generatedContent.find(
        (c: GeneratedContentItem) =>
            c.title.toLowerCase().replace(/\s+/g, '_') === section.id.toLowerCase() ||
            c.title.toLowerCase() === section.id.toLowerCase()
    );

    const hasContent = generatedMatch?.content && generatedMatch.content.trim().length > 0;
    const shouldInclude = hasContent ? true : section.included ?? true;

    return {
        id: section.id,
        title: section.name ?? section.id,
        included: shouldInclude,
        mandatory: section.mandatory ?? false,
        readonly: section.readonly ?? false,
        editing_title: section.editing_title ?? null,
        placeholder: section.placeholder ?? undefined,
        type: mapSectionType(section.type),
        content: section.type === 'GENERATED' && generatedMatch?.content ? generatedMatch.content : '',
    };
};
